import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const ContentModerationLayer = () => {
    const { auth } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [contentDetails, setContentDetails] = useState(null);
    const [contentLoading, setContentLoading] = useState(false);

    const fetchReports = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/moderation/reports`);
            if (!response.ok) throw new Error('Failed to fetch reports');
            const data = await response.json();
            setReports(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching reports:', error);
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleAction = async (id, action) => {
        try {
            const note = notes[id] || '';
            const response = await auth.fetch(`${config.API_BASE_URL}/api/moderation/action`, {
                method: 'POST',
                body: JSON.stringify({ id, action, note }),
            });
            if (response.ok) {
                // Refresh data
                fetchReports();
                // Clear note
                setNotes(prev => ({ ...prev, [id]: '' }));
            }
        } catch (error) {
            console.error('Error performing action:', error);
        }
    };

    const handleNoteChange = (id, value) => {
        setNotes(prev => ({ ...prev, [id]: value }));
    };

    const handleShowEvidence = async (report) => {
        setSelectedReport(report);
        setShowModal(true);
        setContentLoading(true);
        setContentDetails(null);

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/moderation/content/${report.reported_type}/${report.reported_id}`);
            if (response.ok) {
                const data = await response.json();
                setContentDetails(data);
            }
        } catch (error) {
            console.error('Error fetching content:', error);
        } finally {
            setContentLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReport(null);
        setContentDetails(null);
    };

    const getImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${config.API_BASE_URL}/${path.replace(/^\//, '')}`;
    };

    const renderTable = (type) => {
        const filteredReports = reports.filter(r => r.reported_type === type);
        
        if (filteredReports.length === 0) {
            return <div className="p-3 text-center text-secondary">No reports found.</div>;
        }

        return (
            <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                    <thead>
                        <tr>
                            <th scope="col">Reported Item</th>
                            <th scope="col">Reporter</th>
                            <th scope="col">Reason</th>
                            <th scope="col">Evidence</th>
                            <th scope="col">Status</th>
                            <th scope="col">Admin Notes</th>
                            <th scope="col" className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredReports.map(report => (
                            <tr key={report.id}>
                                <td>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h6 className="text-md mb-0 fw-medium">
                                                {type === 'venue' ? `Venue #${report.reported_id}` : 
                                                 type === 'review' ? `Review #${report.reported_id}` : 
                                                 type === 'blog' ? `Blog #${report.reported_id}` :
                                                 `Image #${report.reported_id}`}
                                            </h6>
                                            <span className="text-sm text-secondary-light fw-medium">ID: {report.reported_id}</span>
                                        </div>
                                    </div>
                                </td>
                                <td>{report.reporter_name || `User ${report.reporter_id}`}</td>
                                <td>{report.reason}</td>
                                <td>
                                    <span 
                                        role="button" 
                                        className="text-primary-600 fw-medium" 
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleShowEvidence(report)}
                                    >
                                        View Evidence
                                    </span>
                                </td>
                                <td>
                                    <span className={`badge px-10 py-4 rounded-pill fw-medium text-sm ${
                                        report.status === 'pending' ? 'bg-warning-focus text-warning-main' : 
                                        report.status === 'resolved' ? 'bg-success-focus text-success-main' : 'bg-secondary-focus text-secondary-main'
                                    }`}>
                                        {report.status}
                                    </span>
                                </td>
                                <td>
                                    {report.status === 'pending' ? (
                                        <input 
                                            type="text" 
                                            className="form-control form-control-sm" 
                                            placeholder="Add note..." 
                                            value={notes[report.id] || ''}
                                            onChange={(e) => handleNoteChange(report.id, e.target.value)}
                                        />
                                    ) : (
                                        <span className="text-sm text-secondary">{report.admin_notes}</span>
                                    )}
                                </td>
                                <td className="text-center">
                                    {report.status === 'pending' && (
                                        <div className="d-flex align-items-center gap-2 justify-content-center">
                                            <button onClick={() => handleAction(report.id, 'dismiss')} className="btn btn-sm btn-success-600 radius-8 px-10 py-4">Dismiss</button>
                                            <button onClick={() => handleAction(report.id, 'remove')} className="btn btn-sm btn-danger-600 radius-8 px-10 py-4">Remove</button>
                                            <button onClick={() => handleAction(report.id, 'ban')} className="btn btn-sm btn-outline-danger radius-8 px-10 py-4">Ban User</button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center gap-3">
                    <h6 className="text-lg fw-semibold mb-0">Content & Feedback</h6>
                    <ul className="nav nav-pills border-gradient-tab" id="pills-tab" role="tablist">
                        <li className="nav-item" role="presentation">
                            <button className="nav-link active" id="pills-venues-tab" data-bs-toggle="pill" data-bs-target="#pills-venues" type="button" role="tab">
                                Reported Venues
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="pills-reviews-tab" data-bs-toggle="pill" data-bs-target="#pills-reviews" type="button" role="tab">
                                Reviews
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="pills-blogs-tab" data-bs-toggle="pill" data-bs-target="#pills-blogs" type="button" role="tab">
                                Blogs
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="pills-images-tab" data-bs-toggle="pill" data-bs-target="#pills-images" type="button" role="tab">
                                Images
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="tab-content" id="pills-tabContent">
                    <div className="tab-pane fade show active" id="pills-venues" role="tabpanel">
                        {loading ? 'Loading...' : renderTable('venue')}
                    </div>
                    <div className="tab-pane fade" id="pills-reviews" role="tabpanel">
                        {loading ? 'Loading...' : renderTable('review')}
                    </div>
                    <div className="tab-pane fade" id="pills-blogs" role="tabpanel">
                        {loading ? 'Loading...' : renderTable('blog')}
                    </div>
                    <div className="tab-pane fade" id="pills-images" role="tabpanel">
                        {loading ? 'Loading...' : renderTable('image')}
                    </div>
                </div>
            </div>

            {/* Evidence Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Report Evidence</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {contentLoading ? (
                        <div className="d-flex justify-content-center py-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : selectedReport && (
                        <div>
                            <div className="mb-3">
                                <strong>Reporter:</strong> {selectedReport.reporter_name || `User ${selectedReport.reporter_id}`}
                            </div>
                            <div className="mb-3">
                                <strong>Reported Item:</strong> {selectedReport.reported_type} #{selectedReport.reported_id}
                            </div>
                            
                            {/* Content Preview */}
                            {contentDetails && (
                                <div className="mb-3 p-3 bg-light rounded border">
                                    <h6 className="fw-bold mb-2">Content Preview:</h6>
                                    
                                    {selectedReport.reported_type === 'blog' && (
                                        <div>
                                            {contentDetails.featured_image && (
                                                <img 
                                                    src={getImageUrl(contentDetails.featured_image)} 
                                                    alt="Blog Cover" 
                                                    className="img-fluid rounded mb-2" 
                                                    style={{ maxHeight: '200px', objectFit: 'cover' }}
                                                />
                                            )}
                                            <h5 className="h6 fw-bold">{contentDetails.title}</h5>
                                            <p className="text-sm text-secondary mb-2">{contentDetails.excerpt}</p>
                                            <div className="text-sm bg-white p-2 border rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                {contentDetails.content}
                                            </div>
                                        </div>
                                    )}

                                    {selectedReport.reported_type === 'review' && (
                                        <div>
                                            <div className="d-flex align-items-center mb-2">
                                                <span className="text-warning me-1">★</span>
                                                <strong>{contentDetails.rating}/5</strong>
                                            </div>
                                            <p className="mb-0 fst-italic">"{contentDetails.comment}"</p>
                                        </div>
                                    )}

                                    {selectedReport.reported_type === 'venue' && (
                                        <div>
                                            <h6 className="fw-bold">{contentDetails.business_name}</h6>
                                            <p className="text-sm mb-1">{contentDetails.address}</p>
                                            <p className="text-sm mb-0">{contentDetails.description}</p>
                                        </div>
                                    )}
                                    
                                    {selectedReport.reported_type === 'image' && (
                                        <div>
                                            <img 
                                                src={getImageUrl(contentDetails.image_url)} 
                                                alt="Reported Content" 
                                                className="img-fluid rounded" 
                                                style={{ maxHeight: '300px' }}
                                            />
                                            {contentDetails.caption && <p className="text-center text-secondary mt-1">{contentDetails.caption}</p>}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mb-3">
                                <strong>Reason / Message:</strong>
                                <div className="p-3 bg-light rounded mt-2 border">
                                    {selectedReport.reason}
                                </div>
                            </div>
                            {selectedReport.evidence_url && (
                                <div className="mb-3">
                                    <strong>Additional Evidence:</strong>
                                    <div className="mt-2">
                                        <a href={selectedReport.evidence_url} target="_blank" rel="noopener noreferrer" className="text-primary text-break">
                                            {selectedReport.evidence_url}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ContentModerationLayer;
